import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, ShieldCheck, Calculator, Award, ArrowRight,
  CheckCircle2, Star, Quote, Loader2, Menu, X, Droplets, ChevronDown,
} from "lucide-react";
import { COMPANY, SERVICES, PROCESS, ZONES, TESTIMONIALS, STATS, V2_IMAGES, V2_SERVICE_IMG, V2_REALISATIONS, ALL_CITIES } from "@/data/v2";
import { CITY_LIST } from "@/data/cities";
import { StickyCall } from "@/components/site/StickyCall";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = [
  { q: "Quelles zones desservez-vous ?", a: "Basé à Fréjus, nous intervenons dans tout le Var (Saint-Raphaël, Puget-sur-Argens, Roquebrune-sur-Argens, Draguignan…) et les Alpes-Maritimes (Cannes, Mandelieu, Nice), ainsi que dans toute la région PACA." },
  { q: "Le devis est-il vraiment gratuit ?", a: "Oui, à 100 %. Nous nous déplaçons pour réaliser un diagnostic et vous remettons une estimation claire et détaillée, sans engagement, généralement sous 24 h." },
  { q: "Vos travaux sont-ils garantis ?", a: "Absolument. Tous nos travaux d'étanchéité sont couverts par notre garantie décennale : vous êtes protégé pendant 10 ans." },
  { q: "Quels types d'étanchéité réalisez-vous ?", a: "Étanchéité de toiture terrasse, de balcon, de murs enterrés, isolation thermique de toiture terrasse, résine d'étanchéité, pose de couvertine et pose de dalles sur plots." },
  { q: "Intervenez-vous sur des infiltrations existantes ?", a: "Oui. Nous recherchons l'origine des infiltrations, préparons le support et appliquons la solution d'étanchéité adaptée pour un résultat durable." },
  { q: "Combien de temps dure un chantier ?", a: "Cela dépend de la surface et de la nature des travaux. La plupart des chantiers d'étanchéité sont réalisés en quelques jours. La durée précise figure sur votre devis." },
];

const NAV = [
  { label: "Services", href: "#v2-services" },
  { label: "Réalisations", href: "#v2-realisations" },
  { label: "Méthode", href: "#v2-methode" },
  { label: "Zones", href: "#v2-zones" },
  { label: "FAQ", href: "#v2-faq" },
  { label: "Avis", href: "#v2-avis" },
];

const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

/* ---------------- Navbar ---------------- */
export const NavbarLight = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onS = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onS);
    return () => window.removeEventListener("scroll", onS);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-mh2-border" : "bg-transparent"}`} data-testid="v2-navbar">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <div className="flex flex-col leading-none">
          <span className="font-display font-black text-xl text-mh2-blue">MH</span>
          <span className="font-display text-xs tracking-[0.25em] uppercase text-mh2-terra font-black">Étanchéité</span>
        </div>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((l) => (
            <button key={l.href} onClick={() => scrollTo(l.href)} className="text-sm font-medium text-mh2-slate hover:text-mh2-blue transition-colors" data-testid={`v2-nav-${l.href.slice(4)}`}>
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={`tel:${COMPANY.phone}`} className="hidden sm:flex items-center gap-2 text-sm font-semibold text-mh2-blue" data-testid="v2-nav-phone">
            <Phone size={15} /> {COMPANY.phoneDisplay}
          </a>
          <button onClick={() => scrollTo("#v2-devis")} className="hidden sm:block bg-mh2-terra hover:bg-mh2-terraHover text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors" data-testid="v2-nav-quote">
            Devis gratuit
          </button>
          <button className="lg:hidden text-mh2-ink" onClick={() => setOpen(!open)} data-testid="v2-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-mh2-border px-6 py-5 flex flex-col gap-4">
          {NAV.map((l) => <button key={l.href} onClick={() => { setOpen(false); scrollTo(l.href); }} className="text-left text-lg text-mh2-ink">{l.label}</button>)}
          <button onClick={() => { setOpen(false); scrollTo("#v2-devis"); }} className="bg-mh2-terra text-white font-semibold py-3 rounded-full">Devis gratuit</button>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const HeroLight = () => (
  <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
    <div className="absolute top-0 right-0 w-[45%] h-full bg-mh2-surface -z-10 hidden lg:block rounded-bl-[80px]" />
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-mh2-blue/5 text-mh2-blue text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-full">
          <Droplets size={14} /> Étanchéité & Isolation
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 font-heading font-extrabold tracking-tighter text-mh2-ink text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
          Une toiture <span className="text-mh2-blue whitespace-nowrap">100% étanche</span>,<br />garantie <span className="text-mh2-terra">10 ans</span>.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 text-lg text-mh2-slate max-w-lg leading-relaxed">
          MH Étanchéité protège vos toitures terrasses, balcons et murs enterrés dans le Var,
          les Alpes-Maritimes et toute la région PACA. Devis gratuit sous 24 h.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-9 flex flex-wrap gap-4">
          <button onClick={() => scrollTo("#v2-devis")} className="group inline-flex items-center gap-2 bg-mh2-terra hover:bg-mh2-terraHover text-white font-semibold px-7 py-4 rounded-full transition-colors" data-testid="v2-hero-quote">
            Obtenir mon devis gratuit <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 border-2 border-mh2-blue text-mh2-blue font-semibold px-7 py-4 rounded-full hover:bg-mh2-blue hover:text-white transition-colors" data-testid="v2-hero-call">
            <Phone size={18} /> {COMPANY.phoneDisplay}
          </a>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
        <img src={V2_IMAGES.hero} alt="Villa moderne étanchéité" fetchpriority="high" decoding="async" width="800" height="520" className="w-full h-[420px] lg:h-[520px] object-cover rounded-[32px] shadow-2xl shadow-mh2-blue/20" />
        <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-3 border border-mh2-border">
          <div className="w-12 h-12 rounded-full bg-mh2-green/10 flex items-center justify-center"><ShieldCheck className="text-mh2-green" /></div>
          <div>
            <div className="font-heading font-bold text-mh2-ink leading-tight">Garantie décennale</div>
            <div className="text-xs text-mh2-slate">Travaux assurés 10 ans</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---------------- Trust bar ---------------- */
const TRUST = [
  { icon: ShieldCheck, title: "Garantie décennale", desc: "Sérénité totale 10 ans" },
  { icon: Calculator, title: "Devis 100% gratuit", desc: "Sans engagement, sous 24h" },
  { icon: Award, title: "15+ ans d'expérience", desc: "500+ chantiers réalisés" },
];
export const TrustBar = () => (
  <section className="bg-mh2-surface border-y border-mh2-border">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-10 grid sm:grid-cols-3 gap-8">
      {TRUST.map((t, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-center gap-4" data-testid={`v2-trust-${i}`}>
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center border border-mh2-border shrink-0"><t.icon className="text-mh2-terra" size={22} /></div>
          <div>
            <div className="font-heading font-bold text-mh2-ink">{t.title}</div>
            <div className="text-sm text-mh2-slate">{t.desc}</div>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ---------------- Services (bento) ---------------- */
export const ServicesLight = () => (
  <section id="v2-services" className="max-w-[1300px] mx-auto px-6 lg:px-10 pt-8 pb-10 scroll-mt-20">
    <div className="max-w-2xl mb-10">
      <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-mh2-terra">Nos prestations</span>
      <p className="mt-4 text-mh2-slate text-lg">De la toiture terrasse aux dalles sur plots, un savoir-faire complet et certifié.</p>
    </div>
    <div className="grid md:grid-cols-3 gap-5 auto-rows-[240px]">
      {SERVICES.map((s, i) => {
        const big = i === 0;
        return (
          <motion.article key={s.id}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
            className={`group relative rounded-2xl overflow-hidden border border-mh2-border ${big ? "md:col-span-2 md:row-span-2" : ""}`} data-testid={`v2-service-${s.id}`}>
            <img src={V2_SERVICE_IMG[s.id]} alt={s.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-mh2-ink/85 via-mh2-ink/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6">
              <h3 className={`font-heading font-bold text-white ${big ? "text-2xl md:text-3xl" : "text-xl"}`}>{s.title}</h3>
              <button onClick={() => scrollTo("#v2-devis")} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white w-fit" data-testid={`v2-service-cta-${s.id}`}>
                Demander un devis <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.article>
        );
      })}
    </div>
    <div className="mt-12 flex justify-center">
      <a href={`tel:${COMPANY.phone}`} className="group inline-flex items-center gap-3 bg-mh2-blue hover:bg-mh2-blueDark text-white font-semibold px-8 py-4 rounded-full transition-colors" data-testid="v2-services-call">
        <Phone size={18} className="group-hover:rotate-12 transition-transform" /> Appeler le {COMPANY.phoneDisplay}
      </a>
    </div>
  </section>
);

/* ---------------- Process ---------------- */
export const ProcessLight = () => (
  <section id="v2-methode" className="bg-mh2-blue text-white scroll-mt-20">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-24">
      <div className="max-w-2xl mb-14">
        <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-white/70">Notre méthode</span>
        <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-5xl tracking-tight">Simple, transparent, garanti</h2>
      </div>
      <div className="grid md:grid-cols-4 gap-8">
        {PROCESS.map((p, i) => (
          <motion.div key={p.n} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative" data-testid={`v2-process-${p.n}`}>
            <div className="font-heading font-black text-6xl text-white/15">{p.n}</div>
            <h3 className="mt-2 font-heading font-bold text-xl">{p.title}</h3>
            <p className="mt-2 text-white/70 text-sm leading-relaxed">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Réalisations ---------------- */
const RealisationsLight = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="v2-realisations" className="max-w-[1300px] mx-auto px-6 lg:px-10 pt-10 pb-24 scroll-mt-20">
      <div className="max-w-2xl mb-12">
        <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-mh2-terra">Nos réalisations</span>
        <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-mh2-ink">Des chantiers qui parlent d'eux-mêmes</h2>
        <p className="mt-4 text-mh2-slate text-lg">Un aperçu de nos étanchéités de toitures terrasses, balcons et rénovations réalisées dans le Var et les Alpes-Maritimes.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {V2_REALISATIONS.map((r, i) => (
          <motion.button
            key={i}
            type="button"
            onClick={() => setActive(r)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
            className="group relative overflow-hidden rounded-2xl border border-mh2-border cursor-zoom-in"
            data-testid={`v2-realisation-${i}`}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img src={r.img} alt={r.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-12 flex justify-center">
        <button onClick={() => scrollTo("#v2-devis")} className="group inline-flex items-center gap-2 bg-mh2-terra hover:bg-mh2-terraHover text-white font-semibold px-7 py-4 rounded-full transition-colors" data-testid="v2-realisations-quote">
          Obtenir mon devis gratuit <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-mh2-ink/90 backdrop-blur-sm p-4 sm:p-8 cursor-zoom-out"
            data-testid="v2-lightbox"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 text-white/80 hover:text-white"
              aria-label="Fermer"
              data-testid="v2-lightbox-close"
            >
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              src={active.img}
              alt={active.title}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};


/* ---------------- Zones ---------------- */
const ZonesLight = () => (
  <section id="v2-zones" className="max-w-[1300px] mx-auto px-6 lg:px-10 pt-24 pb-10 scroll-mt-20">
    <div className="max-w-3xl">
      <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-mh2-terra">Zones d'intervention</span>
      <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-mh2-ink">Proche de chez vous, partout en PACA</h2>
      <p className="mt-4 text-mh2-slate text-lg">Basé à Fréjus, nous intervenons dans tout le Var et les Alpes-Maritimes.</p>
    </div>
    <div className="mt-8 flex flex-wrap gap-3">
      {ZONES.map((z, i) => (
        <span key={z} className="inline-flex items-center gap-2 bg-mh2-surface border border-mh2-border px-4 py-2.5 rounded-full text-sm font-medium text-mh2-ink" data-testid={`v2-zone-${i}`}>
          <MapPin size={14} className="text-mh2-terra" /> {z}
        </span>
      ))}
    </div>
    <div className="mt-8 max-w-4xl" data-testid="v2-cities-seo">
      <p className="text-sm text-mh2-slate leading-relaxed">
        <span className="font-semibold text-mh2-ink">Villes desservies :</span> MH Étanchéité réalise vos travaux d'étanchéité de toiture terrasse, de balcon, la résine d'étanchéité, l'isolation thermique et la recherche et réparation de fuite à{" "}
        {CITY_LIST.map((c, i) => (
          <span key={c.slug}>
            <Link to={`/etancheite/${c.slug}`} className="underline decoration-mh2-border hover:text-mh2-blue hover:decoration-mh2-blue transition-colors">{c.name}</Link>{i < CITY_LIST.length - 1 ? ", " : "."}
          </span>
        ))}
      </p>
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-b border-mh2-border" data-testid={`v2-faq-item-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        data-testid={`v2-faq-toggle-${index}`}
      >
        <span className="font-heading font-bold text-lg text-mh2-ink">{item.q}</span>
        <ChevronDown size={20} className={`text-mh2-terra shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-5 text-mh2-slate leading-relaxed max-w-3xl">{item.a}</p>
      </motion.div>
    </div>
  );
};

export const FaqLight = () => (
  <section id="v2-faq" className="max-w-[900px] mx-auto px-6 lg:px-10 pt-10 pb-24 scroll-mt-20">
    <div className="mb-10">
      <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-mh2-terra">Questions fréquentes</span>
      <h2 className="mt-4 font-heading font-extrabold text-2xl sm:text-4xl lg:text-5xl tracking-tight text-mh2-ink whitespace-nowrap">Vos questions, nos réponses</h2>
    </div>
    <div>
      {FAQ.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
    </div>
    <div className="mt-10 flex flex-wrap gap-4">
      <button onClick={() => scrollTo("#v2-devis")} className="inline-flex items-center gap-2 bg-mh2-terra hover:bg-mh2-terraHover text-white font-semibold px-7 py-4 rounded-full transition-colors" data-testid="v2-faq-quote">
        Demander un devis gratuit <ArrowRight size={18} />
      </button>
      <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 border-2 border-mh2-blue text-mh2-blue font-semibold px-7 py-4 rounded-full hover:bg-mh2-blue hover:text-white transition-colors" data-testid="v2-faq-call">
        <Phone size={18} /> {COMPANY.phoneDisplay}
      </a>
    </div>
  </section>
);

/* ---------------- Testimonials + stats ---------------- */
export const TestimonialsLight = () => (
  <section id="v2-avis" className="bg-mh2-surface border-y border-mh2-border scroll-mt-20">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-24">
      <div className="grid sm:grid-cols-4 gap-6 mb-16">
        {STATS.map((s, i) => (
          <div key={i} className="text-center" data-testid={`v2-stat-${i}`}>
            <div className="font-heading font-extrabold text-4xl md:text-5xl text-mh2-blue">{s.value}</div>
            <div className="mt-1 text-sm text-mh2-slate">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="max-w-xl mb-12">
        <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-mh2-terra">Avis clients</span>
        <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-mh2-ink">Ils nous recommandent</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <motion.blockquote key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="bg-white rounded-2xl border border-mh2-border p-8 flex flex-col" data-testid={`v2-testimonial-${i}`}>
            <Quote className="text-mh2-terra mb-4" size={26} />
            <div className="flex gap-1 mb-3">{[...Array(5)].map((_, s) => <Star key={s} size={14} className="fill-mh2-terra text-mh2-terra" />)}</div>
            <p className="text-mh2-ink leading-relaxed flex-1">"{t.text}"</p>
            <footer className="mt-5 pt-5 border-t border-mh2-border">
              <div className="font-heading font-bold text-mh2-ink">{t.name}</div>
              <div className="text-xs uppercase tracking-wide text-mh2-slate mt-0.5">{t.city}</div>
            </footer>
          </motion.blockquote>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Quote form ---------------- */
const inputCls = "w-full mt-1.5 bg-white border border-mh2-border rounded-xl px-4 py-3 text-mh2-ink placeholder:text-mh2-slate/60 focus:border-mh2-blue focus:ring-2 focus:ring-mh2-blue/20 outline-none transition";
export const QuoteFormLight = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Nom, email et téléphone sont requis."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success("Demande envoyée ! Nous vous rappelons rapidement.");
    } catch { toast.error("Erreur. Appelez-nous au " + COMPANY.phoneDisplay); }
    finally { setLoading(false); }
  };
  return (
    <section id="v2-devis" className="max-w-[1300px] mx-auto px-6 lg:px-10 py-24 scroll-mt-20">
      <div className="bg-mh2-blue rounded-[32px] overflow-hidden grid lg:grid-cols-2">
        <div className="p-10 lg:p-14 text-white">
          <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-white/70">Devis gratuit</span>
          <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-4xl tracking-tight">Recevez votre estimation sous 24 h</h2>
          <p className="mt-4 text-white/80 leading-relaxed">Décrivez votre projet, un expert vous rappelle rapidement avec un devis clair et sans engagement.</p>
          <ul className="mt-8 space-y-3 text-white/90">
            {["Déplacement et diagnostic offerts", "Devis détaillé et transparent", "Travaux garantis décennale"].map((x) => (
              <li key={x} className="flex items-center gap-3"><CheckCircle2 size={18} className="text-white" /> {x}</li>
            ))}
          </ul>
          <div className="mt-10 space-y-3 text-sm">
            <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 text-white hover:text-white/80"><Phone size={16} /> {COMPANY.phoneDisplay}</a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-white hover:text-white/80"><Mail size={16} /> {COMPANY.email}</a>
            <p className="flex items-center gap-3 text-white/80"><MapPin size={16} /> {COMPANY.address}, {COMPANY.zip}</p>
          </div>
        </div>
        <div className="bg-white p-8 lg:p-12">
          {done ? (
            <div className="h-full flex flex-col justify-center text-center" data-testid="v2-quote-success">
              <div className="w-16 h-16 rounded-full bg-mh2-green/10 flex items-center justify-center mx-auto"><CheckCircle2 className="text-mh2-green" size={30} /></div>
              <h3 className="mt-5 font-heading font-bold text-2xl text-mh2-ink">Merci !</h3>
              <p className="mt-3 text-mh2-slate">Votre demande est enregistrée. Nous vous recontactons très vite.</p>
              <button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", city: "", service: "", message: "" }); }} className="mt-6 text-mh2-terra font-semibold" data-testid="v2-quote-new">Nouvelle demande</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="v2-quote-form">
              <label className="text-sm font-medium text-mh2-ink">Nom complet *<input className={inputCls} value={form.name} onChange={set("name")} placeholder="Jean Dupont" data-testid="v2-input-name" /></label>
              <label className="text-sm font-medium text-mh2-ink">Téléphone *<input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" data-testid="v2-input-phone" /></label>
              <label className="text-sm font-medium text-mh2-ink">Email *<input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jean@email.fr" data-testid="v2-input-email" /></label>
              <label className="text-sm font-medium text-mh2-ink">Ville<input className={inputCls} value={form.city} onChange={set("city")} placeholder="Fréjus" data-testid="v2-input-city" /></label>
              <label className="sm:col-span-2 text-sm font-medium text-mh2-ink">Prestation
                <select className={inputCls} value={form.service} onChange={set("service")} data-testid="v2-input-service">
                  <option value="">Sélectionnez…</option>
                  {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                </select>
              </label>
              <label className="sm:col-span-2 text-sm font-medium text-mh2-ink">Votre message
                <textarea rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Décrivez votre projet…" data-testid="v2-input-message" />
              </label>
              <button type="submit" disabled={loading} className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-mh2-terra hover:bg-mh2-terraHover disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-colors" data-testid="v2-quote-submit">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />} {loading ? "Envoi…" : "Envoyer ma demande"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
export const FooterLight = () => (
  <footer className="bg-mh2-ink text-white">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-3 gap-10">
      <div>
        <div className="font-display font-black text-2xl">MH ÉTANCHÉITÉ</div>
        <div className="text-xs tracking-[0.3em] uppercase text-mh2-terra font-bold mt-1">Var · Alpes-Maritimes · PACA</div>
        <p className="mt-5 text-white/70 max-w-sm leading-relaxed">Spécialiste étanchéité toiture terrasse, balcon, murs enterrés, isolation thermique, couvertine et dalles sur plots.</p>
      </div>
      <div>
        <h4 className="font-heading font-bold mb-4">Contact</h4>
        <ul className="space-y-3 text-sm text-white/80">
          <li className="flex gap-2"><MapPin size={16} className="text-mh2-terra" /> {COMPANY.address}, {COMPANY.zip}</li>
          <li><a href={`tel:${COMPANY.phone}`} className="flex gap-2 hover:text-white"><Phone size={16} className="text-mh2-terra" /> {COMPANY.phoneDisplay}</a></li>
          <li><a href={`mailto:${COMPANY.email}`} className="flex gap-2 hover:text-white"><Mail size={16} className="text-mh2-terra" /> {COMPANY.email}</a></li>
        </ul>
      </div>
      <div>
        <h4 className="font-heading font-bold mb-4">Prestations</h4>
        <ul className="space-y-2 text-sm text-white/70">{SERVICES.map((s) => <li key={s.id}>{s.title}</li>)}</ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
        <span>© {new Date().getFullYear()} MH Étanchéité — Tous droits réservés.</span>
      </div>
    </div>
  </footer>
);

export const LightHome = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-mh2-cream font-body text-mh2-ink min-h-screen">
      <NavbarLight />
      <main>
        <HeroLight />
        <TrustBar />
        <ServicesLight />
        <RealisationsLight />
        <ProcessLight />
        <ZonesLight />
        <FaqLight />
        <TestimonialsLight />
        <QuoteFormLight />
      </main>
      <FooterLight />
      <StickyCall />
    </div>
  );
};
